import { type ComponentType, lazy } from 'react';

const CHUNK_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Loading chunk .* failed|Failed to load module script/i;

export interface ChunkRecoveryRuntime {
  clearAttempt(): void;
  hasAttempt(): boolean;
  markAttempt(): void;
  reload(): void;
}

export function isDynamicImportError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return CHUNK_ERROR_PATTERN.test(message);
}

function browserRuntime(): ChunkRecoveryRuntime {
  const key = `finance-web:chunk-reload:${window.location.pathname}`;
  return {
    clearAttempt() {
      try {
        window.sessionStorage.removeItem(key);
      } catch {
        // Storage may be unavailable in hardened/private browser contexts.
      }
    },
    hasAttempt() {
      try {
        return window.sessionStorage.getItem(key) === '1';
      } catch {
        // Do not risk an infinite reload loop when the guard cannot persist.
        return true;
      }
    },
    markAttempt() {
      try {
        window.sessionStorage.setItem(key, '1');
      } catch {
        // hasAttempt already prevents reload when storage is unavailable.
      }
    },
    reload() {
      window.location.reload();
    },
  };
}

export async function loadWithChunkRecovery<T>(
  importer: () => Promise<T>,
  runtime: ChunkRecoveryRuntime = browserRuntime(),
): Promise<T> {
  try {
    const module = await importer();
    runtime.clearAttempt();
    return module;
  } catch (error) {
    if (!isDynamicImportError(error) || runtime.hasAttempt()) {
      throw error;
    }
    runtime.markAttempt();
    runtime.reload();
    // Keep React.lazy pending while the browser navigates away. Rejecting here
    // would briefly render the route error page before reload completes.
    return await new Promise<T>(() => undefined);
  }
}

export function lazyWithRecovery<T extends ComponentType>(importer: () => Promise<{ default: T }>) {
  return lazy(() => loadWithChunkRecovery(importer));
}
